// imports
import { Address } from 'abitype';
import { getAccount } from 'wagmi/actions';
import { ethers, InterfaceAbi, TransactionReceipt } from 'ethers';
import { readContract } from '../wagmi';
import { TEXTS } from '../../../constants';
import { IContractProps } from '../wagmi/types';
import { QuestCost } from '../../../common/types';
import ArbNova from '../abi/arbitrum_nova.json';
import ArbGeorli from '../abi/arbitrum_goerli.json';
import { sendQuestActionTransaction } from '../utils';
import {
  IQuestState, IQuestClaimBody, IQuestStartBody, IQuestStartResponse
} from '../types';

// interfaces / types / consts
const L2Chain = (process.env.REACT_APP_ENVIRONMENT === 'prod' || process.env.REACT_APP_ENVIRONMENT === 'stage') ? ArbNova : ArbGeorli;
const WL_QUEST_MANAGER = L2Chain.contracts.QuestsManager as IContractProps;
const WL_CAMPAIGN_PROGRESSION = L2Chain.contracts.CampaignProgression as IContractProps;

// private methods - utils
const getEventLog = (receipt?: TransactionReceipt, event?: string): { log?: ethers.Log; topic?: Address } => {
  if (!receipt || !event || !receipt?.status) { return {}; }
  let topic;
  const signature = ethers.id(event!);
  const log = receipt.logs.find((logP) => {
    topic = logP.topics.find((topicP) => topicP === signature);
    return topic != null;
  });
  return { log, topic };
};

// private methods - response parsing
const parseStartQuestReceipt = (receipt?: TransactionReceipt): IQuestStartResponse | null => {
  const { log, topic } = getEventLog(receipt, 'QuestStarted(string,address,uint256[],uint256,uint256,uint256[4])');
  if (log && topic) {
    const resJSON = ethers.Interface.from(WL_QUEST_MANAGER.abi! as InterfaceAbi).parseLog({ data: log.data, topics: [topic] });
    const { startTime, endTime, rewardChances } = (resJSON || { args: {} }).args as { endTime: bigint; startTime: bigint; rewardChances: bigint[] };
    return { startTime: Number(startTime), endTime: Number(endTime), rewardChances: rewardChances.map((chance) => Number(chance)) };
  }
  return null;
};
const parseClaimRewardsReceipt = (receipt?: TransactionReceipt): QuestCost[] | null => {
  const { log, topic } = getEventLog(receipt, 'QuestEnded(string,address,uint256[],(string,uint256)[])');
  if (log && topic) {
    const resJSON = ethers.Interface.from(WL_QUEST_MANAGER.abi! as InterfaceAbi).parseLog({ data: log.data, topics: [topic] });
    const { rewards } = (resJSON || { args: {} }).args as { rewards: { rewardType: string; amount: bigint }[] };
    const response = [] as QuestCost[];
    rewards.forEach((reward) => {
      const { rewardType: itemId, amount } = reward;
      if (itemId) { response.push({ itemId, amount: Number(amount) }); }
    });
    return response;
  }
  return null;
};

// private methods - contract views
function getInProgressQuestIds(address?: Address): Promise<string[]> {
  return readContract({
    args: [address],
    abi: WL_QUEST_MANAGER.abi,
    chainId: Number(L2Chain.chainId),
    address: WL_QUEST_MANAGER.address,
    functionName: 'getInProgressQuestIds'
  }).then((data) => data as string[]);
}
function getInProgressQuests(questIds: string[], address?: Address): Promise<IQuestState[]> {
  return readContract({
    args: [address, questIds],
    abi: WL_QUEST_MANAGER.abi,
    chainId: Number(L2Chain.chainId),
    address: WL_QUEST_MANAGER.address,
    functionName: 'batchGetInProgressQuests'
  }).then((data) => data as IQuestState[]);
}

// exports - views
export function getPlayersQuests(): Promise<IQuestState[]> {
  const { address } = getAccount();
  return getInProgressQuestIds(address).then((questIds) => getInProgressQuests(questIds, address));
}
export function getCampaignProgression(): Promise<number> {
  const { address } = getAccount();
  return readContract({
    args: [address],
    functionName: 'getUserProgress',
    chainId: Number(L2Chain.chainId),
    abi: WL_CAMPAIGN_PROGRESSION.abi,
    address: WL_CAMPAIGN_PROGRESSION.address
  }).then((data) => Number(data || 0));
}

// exports - writers
export function startQuest(body: IQuestStartBody): Promise<IQuestStartResponse> {
  const { address } = getAccount();
  return sendQuestActionTransaction({ ...body, eoaAddress: address!, action: 'START_QUEST' })
    .then((receipt) => parseStartQuestReceipt(receipt || undefined))
    .then((res) => {
      if (res) { return Promise.resolve(res); }
      return Promise.reject(new Error(TEXTS.ERROR_START_QUEST));
    });
}
export function claimRewards(body: IQuestClaimBody): Promise<QuestCost[]> {
  const { address } = getAccount();
  return sendQuestActionTransaction({ ...body, eoaAddress: address!, action: 'END_QUEST' })
    .then((receipt) => parseClaimRewardsReceipt(receipt || undefined))
    .then((res) => {
      if (res) { return Promise.resolve(res); }
      return Promise.reject(new Error(TEXTS.ERROR_CLAIM_REWARDS));
    });
}

// exports - default
export default {
  startQuest,
  claimRewards,
  getPlayersQuests,
  getCampaignProgression
};
